/**
 * Browse-mode reader — Phase 08, now server-side.
 *
 * One request to `GET /inbox/browse` (a projected UNION of Enquiry /
 * ReportRequest / Report on the server — see
 * `vnext/services/browse_service.py`) returns one page of flat
 * `BrowseEntityRow` records. Search (`q`, codename / family substring),
 * the team filter and pagination all run server-side; the tab renders
 * what it gets.
 *
 * In `cases` mode the server pages codename FAMILIES and returns every
 * member of the page's families, so `BrowseTab` can group a complete
 * case from the page's rows; `total` is then the family count. In `all`
 * mode `total` is the row count.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { ApiInboxBrowseRow, InboxBrowseKind, InboxBrowseMode } from '@/types/inbox';

export type BrowseEntityKind = InboxBrowseKind;

export interface BrowseEntityRow {
  kind: BrowseEntityKind;
  id: string;
  codename: string | null;
  /** Rename key (family root codename) served by the backend — null when
   * the row has no resolvable family. */
  codenameFamily: string | null;
  /**
   * Stage / lifecycle label. For enquiries it's the EnquiryStage; for
   * RRs it's the TriageStage; for Reports the ReportLifecycleStage.
   */
  stageLabel: string;
  teamId: string | null;
  createdAt: string | null;
}

/** Units (case groups / flat rows) per server page. */
export const BROWSE_PAGE_SIZE = 25;

export interface UseBrowseEntitiesParams {
  /** Committed (URL-persisted) codename search. */
  q?: string;
  teamId?: string;
  /** Subset of entity kinds to include. Defaults to all three. */
  kinds?: BrowseEntityKind[];
  mode: InboxBrowseMode;
  page: number;
  pageSize?: number;
  /** When false, no fetching happens at all — the Inbox page keeps the
   * hook mounted across tabs but only the Browse tab should pay (and
   * the shared `q` param must not re-trigger it while the analyst is
   * searching the Queue). */
  enabled?: boolean;
}

interface UseBrowseEntitiesResult {
  /** The current page: flat rows (`all`) or every member of the page's
   * families (`cases`). */
  rows: BrowseEntityRow[];
  /** Rows (`all`) or families (`cases`) across all pages. */
  total: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

function toRow(api: ApiInboxBrowseRow): BrowseEntityRow {
  return {
    kind: api.kind,
    id: api.id,
    codename: api.codename,
    codenameFamily: api.codename_family,
    stageLabel: api.stage,
    teamId: api.team_id,
    createdAt: api.created_at,
  };
}

export function useBrowseEntities(params: UseBrowseEntitiesParams): UseBrowseEntitiesResult {
  const [rows, setRows] = useState<BrowseEntityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const key = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchPage = useCallback(async () => {
    const current = paramsRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.browseEntities({
        q: current.q,
        teamId: current.teamId,
        kinds: current.kinds,
        mode: current.mode,
        page: current.page,
        pageSize: current.pageSize ?? BROWSE_PAGE_SIZE,
      });
      setRows(result.items.map(toRow));
      setTotal(result.total);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paramsRef.current.enabled === false) {
      return;
    }
    void fetchPage();
  }, [key, fetchPage]);

  return { rows, total, isLoading, error, refetch: fetchPage };
}
